// ヤマトCSV → YamatoRow[] への変換

import type { YamatoRow, CsvFileMeta } from "../../types";
import { normalizeName, toIsoDate, toNum } from "../normalize";

// 宅急便単価で計上する数量列（本城さん確定 #31 + 6/19 修正後優先ルール）。
//   [基準列（修正前）, 修正後列] のペア。修正後セルに値(>0)があれば修正後を採用する
//   （締め後の稀な訂正は「修正後」列に乗る＝修正後が正。0/空なら従来どおり修正前）。
//   家財配達は元から修正後のみ（#31）→ ペア相手なし。
const TAKKYU_QTY_PAIRS: [string, string | null][] = [
  ["修正前宅急便配達個数", "修正後宅急便配達個数"],
  ["修正前EAZY配達個数", "修正後EAZY配達個数"],
  ["修正後家財宅急便配達個数", null],
  ["修正前宅急便集荷個数", "修正後宅急便集荷個数"],
  ["修正前家財宅急便集荷個数", "修正後家財宅急便集荷個数"],
];
// ネコポス単価で計上する数量列（本城さん確定 #31）。配完＋持戻＋集荷（ネコポス・ゆうメール）。
const NEKOPOS_QTY_PAIRS: [string, string | null][] = [
  ["修正前ネコポス配完冊数", "修正後ネコポス配完冊数"],
  ["修正前ネコポス持戻冊数", "修正後ネコポス持戻冊数"],
  ["修正前クロネコゆうメール配完冊数", "修正後クロネコゆうメール配完冊数"],
  ["修正前クロネコゆうメール持戻冊数", "修正後クロネコゆうメール持戻冊数"],
  ["修正前ネコポス集荷冊数", "修正後ネコポス集荷冊数"],
  ["修正前クロネコゆうメール集荷冊数", "修正後クロネコゆうメール集荷冊数"],
];

// 必須は基準列のみ（修正後列は任意＝無いCSVでも取り込める）
const REQUIRED_COLS = [
  "稼働日", "委託社員番号", "免許証氏名_姓", "免許証氏名_名",
  ...TAKKYU_QTY_PAIRS.map(([base]) => base),
  ...NEKOPOS_QTY_PAIRS.map(([base]) => base),
];

export function parseYamato(rows: string[][], fileName: string, sizeBytes: number, encoding: string): { meta: CsvFileMeta; yamatoRows: YamatoRow[] } {
  if (rows.length < 2) throw new Error("ヤマトCSV: データ行がありません");
  const header = rows[0];
  const idx = new Map<string, number>();
  header.forEach((h, i) => idx.set(h, i));

  for (const c of REQUIRED_COLS) {
    if (!idx.has(c)) throw new Error(`ヤマトCSV: 必須列「${c}」が見つかりません`);
  }

  const cDate = idx.get("稼働日")!;
  const cCode = idx.get("委託社員番号")!;
  const cLast = idx.get("免許証氏名_姓")!;
  const cFirst = idx.get("免許証氏名_名")!;
  const cTotal = idx.get("合計金額") ?? -1; // ヤマト→HSD請求金額（HSD売上）
  const cOffice = idx.get("営業所コード") ?? -1; // 勝本様の営業所集計（73049）等に使う
  // [基準列idx, 修正後列idx(無ければ-1)] に解決
  const resolvePairs = (pairs: [string, string | null][]): [number, number][] =>
    pairs.map(([base, fixed]) => [idx.get(base)!, fixed != null ? idx.get(fixed) ?? -1 : -1]);
  const takkyuPairs = resolvePairs(TAKKYU_QTY_PAIRS);
  const nekoposPairs = resolvePairs(NEKOPOS_QTY_PAIRS);
  // 修正後セルに値(>0)があれば修正後、無ければ基準（修正前）を合算（6/19 本城さん合意）
  const sumPairs = (row: string[], pairs: [number, number][]) =>
    pairs.reduce((s, [base, fixed]) => {
      const fixedVal = fixed >= 0 ? Number(row[fixed] || 0) || 0 : 0;
      const baseVal = Number(row[base] || 0) || 0;
      return s + (fixedVal > 0 ? fixedVal : baseVal);
    }, 0);

  const out: YamatoRow[] = [];
  let minDate = "";
  let maxDate = "";
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || row.every((c) => !c)) continue;
    const date = toIsoDate(row[cDate]);
    if (!date) continue;
    if (!minDate || date < minDate) minDate = date;
    if (!maxDate || date > maxDate) maxDate = date;
    const last = row[cLast] || "";
    const first = row[cFirst] || "";
    const fullname = `${last}　${first}`.trim();
    out.push({
      workDate: date,
      driverCode: row[cCode] || "",
      driverNameLast: last,
      driverNameFirst: first,
      driverName: fullname,
      driverNameNormalized: normalizeName(fullname),
      officeCode: cOffice >= 0 ? String(row[cOffice] || "").trim() : "",
      takkyubinQty: sumPairs(row, takkyuPairs),
      nekoposQty: sumPairs(row, nekoposPairs),
      totalAmount: cTotal >= 0 ? toNum(row[cTotal]) : 0,
    });
  }

  return {
    meta: {
      fileName,
      carrier: "yamato",
      encoding,
      sizeBytes,
      rows: out.length,
      uploadedAt: new Date().toISOString(),
      periodStart: minDate || null,
      periodEnd: maxDate || null,
    },
    yamatoRows: out,
  };
}
